import { omit } from 'lodash';

import { Declaration, DeclarationDocumentsEnum, TransactionTypes, TransportEquipment } from '@e-origin/shared';

import { toTransactionClass } from './context';

/**
 * The subtree builders the registry uses as `read`.
 *
 * Each produces a whole subtree from several sources, which is why they are functions rather than
 * path pass-throughs. They live in the policy layer so `fields.base.ts` can reference them without
 * a cycle back through `resolve.ts`.
 *
 * Pure: lodash and shared types only, so the node suites still load this island.
 */

/**
 * `isB2BTransaction`, read off the entity alone rather than `ctx.txClass` - `read()` only sees the
 * entity, never the resolution context (see `readIfB2B` below, for the three header-switch fields
 * that are only ever seeded for a B2B declaration).
 */
const isB2BTransaction = (declaration: Declaration) => toTransactionClass(declaration.transactionType) === 'B2B';

/**
 * Wraps a plain `get(entity, path)` read so it stays `undefined` outside a B2B transaction - the
 * three header-switch fields (`countryOfDispatchHeader` etc.) are only ever seeded for a B2B
 * declaration. Only the header switches need this - the value fields they pair with
 * (`countryOfDispatch` etc.) are already policy-owned and gated by `present` instead.
 */
export const readIfB2B =
  <T>(read: (declaration: Declaration) => T) =>
  (entity: unknown): T | undefined => {
    const declaration = entity as Declaration;
    return isB2BTransaction(declaration) ? read(declaration) : undefined;
  };

/**
 * Charges import is read off `globalInfo` and suppressed entirely for B2B_MULTIPLE.
 *
 * That suppression is deliberately kept: `lrn.service.ts:67` tests
 * `!!partialDeclaration.valuationInformationTaxes?.chargesimport` to decide `chargesImportChanged`,
 * so making it unconditional would change a backend decision rather than just a form default.
 */
export const buildChargesImport = (declaration: Declaration) =>
  declaration.transactionType !== TransactionTypes.B2B_MULTIPLE
    ? {
        CustomsCharges: {
          charges: declaration.globalInfo.valuationInformationTaxes?.chargesimport?.CustomsCharges?.charges || 0,
          currency: declaration.globalInfo.valuationInformationTaxes?.chargesimport?.CustomsCharges?.currency,
        },
        VATcharges: {
          charges: declaration.globalInfo.valuationInformationTaxes?.chargesimport?.VATcharges?.charges || 0,
          currency: declaration.globalInfo.valuationInformationTaxes?.chargesimport?.VATcharges?.currency,
        },
      }
    : undefined;

/** Folds `transportEquipment.seals` into the first container - a cross-field merge. */
export const buildContainers = (transportEquipment: TransportEquipment) => {
  const containers = transportEquipment?.containers?.length
    ? transportEquipment.containers.map((container) => ({
        identificationNumber: container.identificationNumber,
        // A fresh copy, not the entity's own array - `.push` below must not mutate the entity.
        seals: [...(container.seals || [])],
      }))
    : [
        {
          identificationNumber: '',
          seals: [],
        },
      ];

  if (transportEquipment?.seals?.length) {
    containers[0].seals.push(...transportEquipment.seals);
  }

  return containers;
};

/** Defaults `header` to true, then keeps only header documents. */
export const buildDocuments = (declaration: Declaration) => {
  const documents = omit(
    declaration.documents,
    declaration.transactionType === TransactionTypes.B2C_HV_BEGATE ? [DeclarationDocumentsEnum.PREVIOUS_DOCUMENTS] : [],
  );

  return Object.keys(documents).reduce(
    (acc, docType) => ({
      ...acc,
      [docType]: (documents[docType] || []).map((doc) => ({ ...doc, header: doc.header === undefined ? true : doc.header })).filter((doc) => doc.header),
    }),
    {},
  );
};

export const buildCommodityCodes = (declaration: Declaration) =>
  declaration.globalInfo.goodShipmentGlobals.map((goodShipmentGlobal) => ({
    riskAnalysis: {
      hsCodeFound: (goodShipmentGlobal as any).hsCodeFound,
      taricData: {},
    },
    firstItemData: {
      hsCode: goodShipmentGlobal.firstItemData.hsCode,
      nationalAdditionalCode: goodShipmentGlobal.firstItemData.nationalAdditionalCode,
      nationalAdditionalCode2: goodShipmentGlobal.firstItemData.nationalAdditionalCode2,
      nationalAdditionalCode3: goodShipmentGlobal.firstItemData.nationalAdditionalCode3,
      taricAdditionalCode: goodShipmentGlobal.firstItemData.taricAdditionalCode,
      taricAdditionalCode2: goodShipmentGlobal.firstItemData.taricAdditionalCode2,
    },
    supportingDocuments: [],
  }));
